import { Spinner, Toggle, Badge, Flashbar, Button, ExpandableSection } from 'AWS-UI-Components-React';

import 'rsuite/dist/styles/rsuite-default.css';
import { Loader, Popover, Whisper, IconButton } from 'rsuite';
import { InfoRound } from '@rsuite/icons/lib/icons';
import { ColumnGroup, Table, Column, HeaderCell, Cell } from 'rsuite-table';

import React from 'react';
import { accumulateProductLineById } from '../../../utils/product_line';
import { getJSClient } from '../../js_client';
import {
  COL,
  DIFF_COL,
  COMPONENT,
  DOWNLOAD_COLUMN_DEFINITIONS,
  DOWNLOAD_COMPONENT_LEVEL_CONNECTOR,
  AGREEMENT_KEY_CONNECTOR,
  TOP_SIZE,
  SUMMARY_TABLE_TYPE,
  SUMMARY_INITIAL_STATE,
  TOP_AGREEMENTS_LABEL,
  PRODUCT_LINES_SUMMARY_DESCRIPTION,
  NET_PPM_WIKI_LINK,
  PCOGS_BRIDGING_FACTORS_LIST,
  QPD_BRIDGING_FACTORS_LIST,
  UNAGGREGATED_FIELD_MAP,
  EXTRA_COLUMN_DEFINITIONS, BPS_COL,
} from './bridging_constants';
import { formatNumber } from '../utils/formatting';
import logger from '../../../utils/logging';
import '../../../styles/custom_rsuite_table.css';
import { deepClone, downloadFile, extractErrorMessage, toDownloadFormat } from '../../../utilities';
import { getShipMonthsFromTimeFrames } from '../../../utils/date-utils.jsx';
import {
  generateKey,
  extractShipMonth,
  extractMarketplaceId,
  parsePercentage,
  extractBridgingFactor,
} from './bridging_utils';
import { getNonAggregatedFieldsWithMultiValues } from '../../../utils/payload';
import DownloadReportDataWindow from '../components/download_report_data_window';

/**
 * Renders bridging report.
 */
export class BridgingReport extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      rows: [],
      rowsAll: { [SUMMARY_TABLE_TYPE.PL]: [], [SUMMARY_TABLE_TYPE.MP]: [] },
      loadDone: false,
      loadingBridging: true,
      loadingAgreement: true,
      loadingSummary: SUMMARY_INITIAL_STATE.BOOLEAN,
      displayInMillions: true,
      endRow: 0,
      endRowAll: SUMMARY_INITIAL_STATE.NUM,
      totalRows: 0,
      totalRowsAll: SUMMARY_INITIAL_STATE.NUM,
      error: false,
      errorMessage: '',
      componentSummaryExpanded: true,
      plSummaryExpanded: true,
      mpSummaryExpanded: true,
      downloadInProgress: false,
      downloadAgreementInProgress: false,
      expandedRowKey: [],
      downloadDataWindowVisible: false,
    };
    this.payload = props.payload;
    this.payloadAll = {
      [SUMMARY_TABLE_TYPE.PL]: { ...props.payload, groupByProductLine: true },
      [SUMMARY_TABLE_TYPE.MP]: { ...props.payload, groupByMarketplaceId: true },
    };
    this.productLineMap = {};
    this.marketplaceMap = {};
    this.agreementMap = new Map();
    this.agreementBridgingFactorMap = new Map();
    this.productLines = props.productLines;
    this.marketplaceSummary = !this.payload.marketplace;
    this.nonAggregatedFlag = this.props.requestPayloads.length > 1;
    if (this.nonAggregatedFlag) {
      this.dataSetKeys = getNonAggregatedFieldsWithMultiValues(this.props.requestPayloads).map(
        (field) => UNAGGREGATED_FIELD_MAP[field]
      );
    }
    // TODO: Remove the Beta Feature control of Bridging Summary after Finance validated
    this.summaryViewAccess = this.props.featureList.includes('bridging-summary') && !this.nonAggregatedFlag;
    this.accessiblePCogsFactors = this.getAccessibleFactors(PCOGS_BRIDGING_FACTORS_LIST);
    this.accessibleQPDFactors = this.getAccessibleFactors(QPD_BRIDGING_FACTORS_LIST);
  }

  componentDidMount = () => {
    this.initialClientTasks();
  };

  initialClientTasks = () => {
    this.client = getJSClient();
    accumulateProductLineById(this.productLines, null, this.productLineMap);
    this.prepareMarketplaceMap();
    this.payload.timeFrame = getShipMonthsFromTimeFrames(this.payload.timeFrame).join();
    this.payloadAll[SUMMARY_TABLE_TYPE.PL].productLines = this.getProductLine();
    this.startFetchingBridgingFactors();
  };

  prepareMarketplaceMap = () => {
    this.props.marketplaceItems.forEach((marketplace) => {
      this.marketplaceMap[marketplace.id] = { ...marketplace };
    });
  };

  postAllLoadTasks = () => {
    if (!this.state.loadingBridging && !this.state.loadingAgreement) {
      this.addAgreementData();
    }
    this.setState({
      loadDone:
        !this.state.loadingBridging &&
        !this.state.loadingAgreement &&
        !this.state.loadingSummary[SUMMARY_TABLE_TYPE.PL] &&
        !this.state.loadingSummary[SUMMARY_TABLE_TYPE.MP],
    });
  };

  startFetchingBridgingFactors = () => {
    let loadingSummary = {
      [SUMMARY_TABLE_TYPE.PL]: this.summaryViewAccess,
      [SUMMARY_TABLE_TYPE.MP]: this.summaryViewAccess && this.marketplaceSummary,
    };
    this.setState({
      loadingBridging: true,
      loadingAgreement: false,
      loadingBridgingPl: true,
      loadingSummary,
      totalRows: 0,
      totalRowsAll: SUMMARY_INITIAL_STATE.NUM,
      endRow: 0,
      endRowAll: SUMMARY_INITIAL_STATE.NUM,
      rows: [],
      rowsAll: { [SUMMARY_TABLE_TYPE.PL]: [], [SUMMARY_TABLE_TYPE.MP]: [] },
      error: false,
    });
    this.loadedPayloadCnt = 0;

    this.props.requestPayloads.forEach((payload) => {
      payload.timeFrame = getShipMonthsFromTimeFrames(payload.timeFrame).join();
      this.client.getBridgingFactorsReport(this.onBridgingFactorsPull, 0, this.onFailedRequest, payload);
    });

    if (this.summaryViewAccess) {
      this.client.getBridgingFactorsReport(
        this.onBridgingFactorsSummaryPull,
        0,
        this.onFailedRequest,
        this.payloadAll[SUMMARY_TABLE_TYPE.PL],
        SUMMARY_TABLE_TYPE.PL
      );
      if (this.marketplaceSummary) {
        this.client.getBridgingFactorsReport(
          this.onBridgingFactorsSummaryPull,
          0,
          this.onFailedRequest,
          this.payloadAll[SUMMARY_TABLE_TYPE.MP],
          SUMMARY_TABLE_TYPE.MP
        );
      }
    }
  };

  onBridgingFactorsAgreementPull = (data = {}) => {
    //TODO: clean up and refactor how agreements data is stored and saved.
    data.rows.forEach((agreement) => {
      const agreementMarketplaceKey = generateKey(
        [agreement.shipMonth, agreement.marketplaceId, agreement.bridgingFactor.toUpperCase()],
        AGREEMENT_KEY_CONNECTOR
      );
      const agreementGlobalKey = generateKey(
        [agreement.shipMonth, 0, agreement.bridgingFactor.toUpperCase()],
        AGREEMENT_KEY_CONNECTOR
      );
      const agreementAllMonthsKey = generateKey(
        [0, agreement.marketplaceId, agreement.bridgingFactor.toUpperCase()],
        AGREEMENT_KEY_CONNECTOR
      );
      const agreementAllMonthsGlobalKey = generateKey(
        [0, 0, agreement.bridgingFactor.toUpperCase()],
        AGREEMENT_KEY_CONNECTOR
      );

      [agreementMarketplaceKey, agreementGlobalKey, agreementAllMonthsKey, agreementAllMonthsGlobalKey].forEach(
        (key) => {
          if (this.agreementMap.has(key)) {
            this.agreementMap.get(key).push(agreement);
          } else {
            this.agreementMap.set(key, [agreement]);
          }
        }
      );
    });

    const { rows } = this.state;
    let verticalRows = this.generateVerticalRows(rows);

    this.addAgreementData();
    this.setState({ verticalRows, loadingBridging: false, loadingAgreement: false }, this.postAllLoadTasks);
  };

  onBridgingFactorsPull = (data = {}, page, payload, type) => {
    let loadingBridging = false;
    logger.info(`Fetched bridging factors for page: ${page}`);

    this.preprocessingData(data, payload);

    if (data.endRow < data.totalRows - 1) {
      loadingBridging = true;
      this.client.getBridgingFactorsReport(this.onBridgingFactorsPull, page + 1, this.onFailedRequest, payload);
    } else {
      loadingBridging = ++this.loadedPayloadCnt !== this.props.requestPayloads.length;
    }

    const { totalRows, endRow } = data;
    let rows = [...this.state.rows, ...data.rows];
    this.setState({ rows, totalRows, endRow });

    if (!loadingBridging) {
      let verticalRows = this.generateVerticalRows(rows);
      if (this.nonAggregatedFlag) {
        let unAggRow = deepClone(verticalRows[0]);
        this.unAggKeys = [];
        for (let i = 0; i < verticalRows.length; i = i + 2) {
          let netPpmRow = verticalRows[i];
          let suffix = generateKey(this.dataSetKeys.map((key) => netPpmRow[key]));
          this.unAggKeys.push(suffix);
          this.unAggKeys.sort();
          this.addUnaggregateColumn(unAggRow, netPpmRow, suffix);
        }
        this.setState({ unAggRows: [unAggRow] });
      }
      let componentSummaryRows = [];
      let expandedRowKeys = [];

      verticalRows.forEach((row) => {
        componentSummaryRows = [...componentSummaryRows, this.prepareComponentSummaryRows(row)];
        expandedRowKeys.push(row.key);
      });

      this.setState(
        { verticalRows: verticalRows, componentSummaryRows, loadingBridging, expandedRowKeys },
        this.postAllLoadTasks
      );
    }
  };

  onBridgingFactorsSummaryPull = (data = {}, page, payload, type) => {
    let loading = false;
    logger.info(`Fetched bridging factors summary by ${type} for page: ${page}`);

    this.preprocessingData(data, payload);

    if (data.endRow < data.totalRows - 1) {
      loading = true;
      this.client.getBridgingFactorsReport(
        this.onBridgingFactorsSummaryPull,
        page + 1,
        this.onFailedRequest,
        payload,
        type
      );
    }

    let rows = [...this.state.rowsAll[type], ...data.rows];
    let { rowsAll, totalRowsAll, endRowAll, loadingSummary } = this.state;
    rowsAll[type] = rows;
    totalRowsAll[type] = data.totalRows;
    endRowAll[type] = data.endRow;
    this.setState({ rowsAll, totalRowsAll, endRowAll });

    if (!loading) {
      let newRows = this.generateVerticalRows(rows);
      rowsAll[type] = newRows
        .sort((rowA, rowB) => Math.abs(rowB[DIFF_COL.NET_VALUE.key]) - Math.abs(rowA[DIFF_COL.NET_VALUE.key]))
        .slice(0, TOP_SIZE);
      loadingSummary[type] = loading;
      this.setState({ rowsAll, loadingSummary }, this.postAllLoadTasks);
    }
  };

  preprocessingData = (data, payload) => {
    data.rows.forEach((row) => {
      for (let [key, item] of Object.entries(row)) {
        item.productLine = (this.productLineMap[item.productLineId] || {}).name;
        item.productLines = (this.productLineMap[payload.productLines] || {}).name;
        item.marketplace = (this.marketplaceMap[item.marketplaceId] || {}).detail;
        item['key'] = `${item.shipMonth}-${item.marketplaceId}-${item.productLineId}-${key}`;
        item.percentage = parsePercentage(item.percentage);
      }
    });
  };

  getProductLine = () => {
    const productLines = this.payloadAll[SUMMARY_TABLE_TYPE.PL].productLines;
    return !(this.productLineMap[productLines] || {}).isLeaf
      ? productLines
      : (this.productLineMap[productLines] || {}).parent;
  };

  filterApplicableMarketplace = (factor, payload) => {
    return (
      !factor.marketplaces ||
      !payload.marketplace ||
      payload.marketplace.split(',').filter((m) => factor.marketplaces.includes(parseInt(m))).length !== 0
    );
  };

  getAccessibleFactors = (factors) => {
    const features = this.props.featureList.map((f) => f.toLowerCase());
    return factors
      .map((f) => {
        return {
          ...f,
          factors: Object.keys(f.factors)
            .filter((factorKey) => features.includes(factorKey.toLowerCase()))
            .map((factorKey) => f.factors[factorKey])
            .reduce((acc, factor) => {
              acc[factor.key] = factor;
              return acc;
            }, {}),
        };
      })
      .filter((f) => Object.keys(f.factors).length > 0);
  };

  generateVerticalRows = (rows) => {
    let newRows = [];
    rows.forEach((row) => {
      let newRow = {
        ...row[COMPONENT.NET_PPM.key],
        title: COMPONENT.NET_PPM.title,
        children: [COMPONENT.PRODUCT_REVENUE,
                    COMPONENT.SALES_DISCOUNTS,
                    COMPONENT.CONTRA_COGS_VFCC,
                    COMPONENT.PRODUCT_COGS,
                    COMPONENT.QUICK_PAY_DISCOUNTS
        ].map(
          (component) => {
            return {
              ...row[component.key],
              title: component.title,
              children: this.generateFactor(component.factors, row),
            };
          }
        ),
      };
      newRows.push(newRow);

      let gmsRow = {
        ...row['GMS'],
        title: 'GMS',
      }

      newRows.push(gmsRow);
    });
    return newRows;
  };

  generateFactor = (factors, row) => {
    return Object.keys(factors)
      .filter((key) => this.filterApplicableMarketplace(factors[key], this.payload))
      .map((key) => {
        let child = {
          ...row[key],
          title: factors[key].title,
        };
        if (!this.nonAggregatedFlag && factors[key].hasAgreement) {
          child = { ...child, originalComponent: child.component };
          this.agreementBridgingFactorMap.set(
            generateKey([extractShipMonth(child.key), extractMarketplaceId(child.key), key], AGREEMENT_KEY_CONNECTOR),
            child
          );
        }
        return child;
      });
  };

  prepareComponentSummaryRows = (netPPMRow) => {
    let row = deepClone(netPPMRow);
    if (row.children) {
      row.children.forEach((child) => {
        child.children = null;
      });
    }
    return row;
  };

  addUnaggregateColumn = (unAggRow, row, suffix) => {
    Object.values(COL).forEach((col) => {
      unAggRow[generateKey([col.key, suffix])] = row[col.key];
    });

    if (row.children && unAggRow.children) {
      for (let i = 0; i < row.children.length; i++) {
        this.addUnaggregateColumn(unAggRow.children[i], row.children[i], suffix);
      }
    }
  };

  addAgreementData = () => {
    for (let key of this.agreementBridgingFactorMap.keys()) {
      let row = this.agreementBridgingFactorMap.get(key);
      if (this.agreementMap.has(key)) {
        row.children = this.agreementMap.get(key).map((agreement) => {
          return {
            ...agreement,
            title: (
              <div>
                <span>{agreement.vendorName || ''}</span>
                <span style={{ fontSize: '12px' }}>{' Agreement#' + agreement.agreementId}</span>
              </div>
            ),
            isAgreementData: true,
          };
        });
      } else {
        const hasNonZeroFactorValue =
          (row.cpValue && Math.abs(row.cpValue) > 0) || (row.cognosValue && Math.abs(row.cognosValue) > 0);
        if (hasNonZeroFactorValue) {
          row.children = [];
        }
      }
    }
  };

  onFailedRequest = (err) => {
    this.setState({ error: true, loading: false, errorMessage: extractErrorMessage(err) });
  };

  onComponentSummaryExpand = () => {
    this.setState({ componentSummaryExpanded: !this.state.componentSummaryExpanded });
  };

  onPlSummaryExpand = () => {
    this.setState({ plSummaryExpanded: !this.state.plSummaryExpanded });
  };

  onMpSummaryExpand = () => {
    this.setState({ mpSummaryExpanded: !this.state.mpSummaryExpanded });
  };

  onToggleMillions = (chosen) => {
    this.setState({ displayInMillions: chosen.detail.checked });
  };

  traversal = (row, data, depth) => {
    if (row === null || row.isAgreementData) {
      return;
    }
    row.title = DOWNLOAD_COMPONENT_LEVEL_CONNECTOR.repeat(depth) + row.title;
    data.push(row);
    if (row.children) {
      row.children.forEach((child) => this.traversal(child, data, depth + 1));
    }
  };

  prepareDownloadData = (preRows) => {
    let data = [];
    let rows = [...preRows];
    rows.forEach((row) => {
      this.traversal(row, data, 0);
    });
    return data;
  };

  onDownloadData = () => {
    this.setState({ downloadInProgress: true });
    logger.info('Downloading Bridging Report.');
    if (!this.nonAggregatedFlag) {
      this.downloadData(
        toDownloadFormat(DOWNLOAD_COLUMN_DEFINITIONS, this.prepareDownloadData(this.state.verticalRows))
      );
    } else {
      this.setState({ downloadDataWindowVisible: true });
    }
  };

  downloadData = (data) => {
    downloadFile(data, 'bridging_report.xls');
    this.setState({ downloadInProgress: false });
  };
  onDownloadDataWindowCancel = () => {
    this.setState({ downloadDataWindowVisible: false });
  };

  onDownloadDataWindowSubmit = (downloadType) => {
    this.setState({ downloadDataWindowVisible: false });
    if (downloadType === 'rows') {
      let columnDefinitions = [
        ...DOWNLOAD_COLUMN_DEFINITIONS,
        ...this.dataSetKeys.map((key) => EXTRA_COLUMN_DEFINITIONS[key]),
      ];
      this.downloadData(toDownloadFormat(columnDefinitions, this.prepareDownloadData(this.state.verticalRows)));
    } else {
      let columnDefinitions = this.getNonAggColumnDefinitions();
      this.downloadData(toDownloadFormat(columnDefinitions, this.prepareDownloadData(this.state.unAggRows)));
    }
  };

  onDownloadAgreementData = () => {
    this.setState({ downloadAgreementInProgress: true });
    this.client.downloadBridgingAgreementReport(
      (report) => {
        this.setState({ downloadAgreementInProgress: false });
        window.open(report.s3Location, '_blank');
      },
      (error) => {
        this.setState({ downloadAgreementInProgress: false });
        this.onFailedRequest(error);
      },
      {
        ...this.payload,
        betaFactorsCsv: this.accessiblePCogsFactors
          .map((f) => Object.keys(f.factors))
          .flat()
          .join(','),
      }
    );
  };

  MonetaryCell = ({ rowData, dataKey, ...props }) => (
    <Cell {...props}>
      <div style={{ textAlign: 'middle' }}>
        {formatNumber(rowData[dataKey], 'monetary', props.millions === 'true', true)}
      </div>
    </Cell>
  );

  BpsCell = ({ rowData, dataKey, ...props }) => (
      <Cell {...props}>
        <div style={{ textAlign: 'middle' }}>
          {formatNumber(rowData[dataKey], 'decimal', props.millions === 'false', true)}
        </div>
      </Cell>
  );

  handleOnExpandChange = (isOpen, rowData) => {
    const hasChildren = rowData.children && rowData.children.length !== 0;
    if (hasChildren) {
      // Children already loaded - no need to reload
      return;
    }

    rowData.loading = isOpen;
    const payload = { ...this.payload, bridgingFactor: extractBridgingFactor({ key: rowData.key }) };
    this.client.getBridgingFactorsAgreementsReport(this.onBridgingFactorsAgreementPull, this.onFailedRequest, payload);
  };

  handleRenderTreeToggle = (icon, rowData, expanded) => {
    const isLoadingChildren = rowData.loading && rowData.children && rowData.children.length === 0;
    if (isLoadingChildren) {
      return <Loader />;
    }

    if (rowData.originalComponent) {
      rowData.component = !expanded ? (
        rowData.originalComponent
      ) : (
        <span>
          {rowData.originalComponent}
          <span style={{ fontSize: '12px', color: 'gray', fontStyle: 'italic', alignContent: 'right' }}>
            {' (' + TOP_AGREEMENTS_LABEL + ')'}
          </span>
        </span>
      );
    }

    return icon;
  };

  getNonAggColumnDefinitions = () => {
    let colDefs = [];
    DOWNLOAD_COLUMN_DEFINITIONS.forEach((colDef) => {
      if (colDef.excludedInNonAgg) {
        colDefs = [...colDefs, ...[colDef]];
      } else {
        colDefs = [
          ...colDefs,
          ...this.unAggKeys.map((key) => {
            return {
              header: colDef.header + `[${key}]`,
              id: generateKey([colDef.id, key]),
            };
          }),
        ];
      }
    });
    return colDefs;
  };

  getCpColumn = () => {
    if (this.nonAggregatedFlag) {
      return this.unAggKeys.map((key) => {
        return this.getCpCognosBaseColumn(generateKey(['cpValue', key]), `CP Value[${key}]`);
      });
    } else {
      return this.getCpCognosBaseColumn('cpValue', 'CP Value');
    }
  };

  getCognosColumn = () => {
    if (this.nonAggregatedFlag) {
      return this.unAggKeys.map((key) => {
        return this.getCpCognosBaseColumn(generateKey(['cognosValue', key]), `P&L Actuals[${key}]`);
      });
    } else {
      return this.getCpCognosBaseColumn('cognosValue', 'P&L Actuals');
    }
  };

  getCpCognosBaseColumn = (dataKey, title) => {
    return (
      <Column width={200} resizable>
        <HeaderCell style={{ fontSize: '14px' }} verticalAlign="middle" align="center">
          {title}
        </HeaderCell>
        <this.MonetaryCell align="center" dataKey={dataKey} millions={this.state.displayInMillions.toString()} />
      </Column>
    );
  };

  getDiffColumn = (col) => {
    if (this.nonAggregatedFlag) {
      return this.unAggKeys.map((key) => {
        return this.getDiffBaseColumn(generateKey([col.key, key]), `${col.title}[${key}]`, col);
      });
    } else {
      return this.getDiffBaseColumn(col.key, col.title, col);
    }
  };

  getDiffBaseColumn = (dataKey, title, col) => {
    if (col.displayInMillions) {
      return (
        <Column key={dataKey} width={200} resizable>
          <HeaderCell style={{ backgroundColor: 'ghostwhite', fontSize: '14px' }} verticalAlign="middle" align="center">
            {title}
          </HeaderCell>
          <this.MonetaryCell align="center" dataKey={dataKey} millions={this.state.displayInMillions.toString()} />
        </Column>
      );
    } else {
      return (
        <Column key={dataKey} width={200} resizable>
          <HeaderCell style={{ backgroundColor: 'ghostwhite', fontSize: '14px' }} verticalAlign="middle" align="center">
            {title}
          </HeaderCell>
          <Cell align="center" dataKey={dataKey} />
        </Column>
      );
    }
  };

  getBpsColumn = (col) => {
    if (this.nonAggregatedFlag) {
      return this.unAggKeys.map((key) => {
        return this.getBpsBaseColumn(generateKey([col.key, key]), `${col.title}[${key}]`);
      });
    } else {
      return this.getBpsBaseColumn(col.key, col.title);
    }
  };

  getBpsBaseColumn = (dataKey, title) => {
    return (
        <Column key={dataKey} width={200} resizable>
          <HeaderCell style={{fontSize: '14px' }} verticalAlign="middle" align="center">
            {title}
          </HeaderCell>
          <this.BpsCell align="center" dataKey={dataKey} />
        </Column>
    );
  };

  getVerticalViewTable = (data, height, firstHeaderName, firstColumnKey = 'title', isTree = true, headerPopover) => {
    const { expandedRowKeys } = this.state;

    return (
      <Table
        data={data}
        rowKey="key"
        height={height}
        affixHeader
        virtualized
        headerHeight={70}
        isTree={isTree}
        shouldUpdateScroll={false}
        onExpandChange={this.handleOnExpandChange}
        renderTreeToggle={this.handleRenderTreeToggle}
        defaultExpandedRowKeys={expandedRowKeys}
      >
        <Column width={400} fixed resizable treeCol>
          <HeaderCell style={{ fontSize: '14px' }} verticalAlign="middle">
            {firstHeaderName}
            {headerPopover && headerPopover}
          </HeaderCell>
          <Cell dataKey={firstColumnKey} />
        </Column>
        {this.getCpColumn()};{this.getCognosColumn()};
        {/*TODO: BUG Appears while using ColumnGroup in nonAggregate data*/}
        {this.nonAggregatedFlag &&
          Object.values(DIFF_COL).map((col) => {
            return this.getDiffColumn(col);
          })}
        {!this.nonAggregatedFlag && (
          <ColumnGroup header="Diff" align="center">
            {Object.values(DIFF_COL).map((col) => {
              return this.getDiffColumn(col);
            })}
          </ColumnGroup>
        )}
        {this.nonAggregatedFlag &&
            Object.values(BPS_COL).map((col) => {
              return this.getBpsColumn(col);
            })}
        {!this.nonAggregatedFlag && (
            <ColumnGroup header="BPS" align="center">
              {Object.values(BPS_COL).map((col) => {
                return this.getBpsColumn(col);
              })}
            </ColumnGroup>
        )}
      </Table>
    );
  };

  getWikiLinkPopover = () => {
    const speaker = (
      <Popover>
        <span className="awsui-util-font-size-2">
          <a href={NET_PPM_WIKI_LINK} target="_blank">
            Net PPM Bridging Factors Data Dictionary
          </a>
        </span>
      </Popover>
    );

    return (
      <Whisper placement="top" trigger="click" controlId="control-id-click" speaker={speaker} placement="right">
        <IconButton size="xs" appearance="subtle" icon={<InfoRound />} />
      </Whisper>
    );
  };

  render() {
    const {
      loadDone,
      error,
      errorMessage,
      displayInMillions,
      endRow,
      totalRows,
      verticalRows,
      unAggRows,
      plSummaryExpanded,
      mpSummaryExpanded,
      rowsAll,
      downloadDataWindowVisible,
    } = this.state;
    return (
      <div className="awsui-util-ph-xs">
        <div className="awsui-util-container">
          <div className="awsui-util-container-header">
            <div className="awsui-util-action-stripe">
              <div className="awsui-util-action-stripe-title">
                {!loadDone && (
                  <Badge color="blue">
                    {' '}
                    <Spinner size="normal" /> {totalRows === 0 ? 'Loading' : `Loaded ${endRow} of ${totalRows} records`}{' '}
                  </Badge>
                )}
                {error && (
                  <Flashbar
                    items={[
                      {
                        header: `Failed to get bridging factors: ${errorMessage}`,
                        type: 'error',
                        buttonText: 'Retry',
                        buttonClick: () => this.startFetchingBridgingFactors(),
                        dismissible: true,
                      },
                    ]}
                  />
                )}
              </div>

              {downloadDataWindowVisible && (
                <DownloadReportDataWindow
                  onCancel={this.onDownloadDataWindowCancel}
                  onSubmit={this.onDownloadDataWindowSubmit}
                />
              )}

              <div className="awsui-util-action-stripe-group">
                <div className="awsui-util-p-s">
                  <Toggle onChange={this.onToggleMillions} checked={displayInMillions}>
                    Display in Millions
                  </Toggle>
                </div>
                {!this.nonAggregatedFlag && (
                  <div className="awsui-util-p-s">
                    <Button
                      text="Download Agreement"
                      onClick={this.onDownloadAgreementData}
                      icon="download"
                      loading={this.state.downloadAgreementInProgress}
                    />
                  </div>
                )}
                <div className="awsui-util-p-s">
                  <Button
                    text="Download"
                    onClick={this.onDownloadData}
                    icon="download"
                    loading={this.state.downloadInProgress}
                  />
                </div>
              </div>
            </div>
          </div>

          {loadDone && (
            <div>
              {this.summaryViewAccess && (
                <div>
                  <div style={{ padding: '0px' }}>
                    <ExpandableSection
                      header={'Component Summary'}
                      onChange={this.onComponentSummaryExpand}
                      expanded={this.state.componentSummaryExpanded}
                    >
                      {this.getVerticalViewTable(this.state.componentSummaryRows, 400)}
                    </ExpandableSection>
                  </div>

                  {this.marketplaceSummary && (
                    <div style={{ padding: '0px' }}>
                      <ExpandableSection
                        header={'Marketplace Summary'}
                        onChange={this.onMpSummaryExpand}
                        expanded={mpSummaryExpanded}
                      >
                        {this.getVerticalViewTable(
                          rowsAll[SUMMARY_TABLE_TYPE.MP],
                          400,
                          'Marketplace',
                          'marketplace',
                          false
                        )}
                      </ExpandableSection>
                    </div>
                  )}

                  <div style={{ padding: '0px' }}>
                    <ExpandableSection
                      header={'Product Line Summary'}
                      onChange={this.onPlSummaryExpand}
                      expanded={plSummaryExpanded}
                    >
                      <span style={{ fontSize: '12px', color: 'gray', fontStyle: 'italic', alignContent: 'right' }}>
                        {PRODUCT_LINES_SUMMARY_DESCRIPTION}
                      </span>
                      {this.getVerticalViewTable(
                        rowsAll[SUMMARY_TABLE_TYPE.PL],
                        400,
                        'Product Line',
                        'productLine',
                        false
                      )}
                    </ExpandableSection>
                  </div>
                </div>
              )}

              <div style={{ padding: '0px' }}>
                {this.getVerticalViewTable(
                  this.nonAggregatedFlag ? unAggRows : verticalRows,
                  700,
                  'Component Tree',
                  'title',
                  true,
                  this.getWikiLinkPopover()
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/reports/bridging/bridging_report.jsx